import {
  AFFILIATION_FILTER_ALL,
  getAffiliationShortLabelFromRef,
  resolveCompanyAffiliation
} from '../../utils/affiliationHelpers';
import { resolveCompanyCity } from '../../utils/cityHelpers';

export const CITY_FILTER_ALL = '__all_cities__';

export const COMPANY_STATUS_FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'active', label: 'Actives' },
  { id: 'inactive', label: 'Inactives' }
];

export function filterCompanies(
  companies,
  { search = '', statusFilter = 'all', affiliationFilter = AFFILIATION_FILTER_ALL, cityFilter = CITY_FILTER_ALL, affiliations = [], cities = [] } = {}
) {
  return companies.filter((company) => {
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && company.is_active) ||
      (statusFilter === 'inactive' && !company.is_active);
    const matchesAffiliation =
      affiliationFilter === AFFILIATION_FILTER_ALL ||
      String(company.affiliation_id) === String(affiliationFilter);
    const matchesCity =
      cityFilter === CITY_FILTER_ALL || String(company.city_id) === String(cityFilter);
    return (
      matchesCompanySearch(company, search, affiliations, cities) &&
      matchesStatus &&
      matchesAffiliation &&
      matchesCity
    );
  });
}

export function hasCompanyListFilters({
  search = '',
  statusFilter = 'all',
  affiliationFilter = AFFILIATION_FILTER_ALL,
  cityFilter = CITY_FILTER_ALL
} = {}) {
  return (
    Boolean(String(search).trim()) ||
    statusFilter !== 'all' ||
    affiliationFilter !== AFFILIATION_FILTER_ALL ||
    cityFilter !== CITY_FILTER_ALL
  );
}

export function computeCompanyListStats(filteredCompanies, repositoryTotal = 0) {
  const total = filteredCompanies.length;
  const active = filteredCompanies.filter((c) => c.is_active).length;
  const inactive = total - active;
  const activePct = total > 0 ? Math.round((active / total) * 100) : 0;
  return {
    total,
    active,
    inactive,
    activePct,
    repositoryTotal: repositoryTotal ?? total
  };
}

export function buildCompanyListStats(filteredCompanies, repositoryTotal, filters) {
  const base = computeCompanyListStats(filteredCompanies, repositoryTotal);
  return {
    ...base,
    isFiltered: hasCompanyListFilters(filters) || base.total !== repositoryTotal
  };
}

export const DEFAULT_COMPANY_LIST_FILTERS = {
  search: '',
  statusFilter: 'all',
  affiliationFilter: AFFILIATION_FILTER_ALL,
  cityFilter: CITY_FILTER_ALL
};

export function buildEmptyCompanyForm(defaultAffiliation, defaultCity) {
  return {
    company_code: '',
    name: '',
    address: '',
    city_id: defaultCity?.id ?? '',
    phone: '',
    mail: '',
    affiliation_id: defaultAffiliation?.id ?? ''
  };
}

export function formatCompanyContactLine(company) {
  const mail = (company?.mail || '').trim();
  const phone = (company?.phone || '').trim();
  if (mail && phone) return `${mail} · ${phone}`;
  return mail || phone || null;
}

export function formatCompanyLocationLine(company, cities = []) {
  const cityRef = resolveCompanyCity(company, cities);
  const city = (cityRef?.name || company?.city || '').trim();
  const address = (company?.address || '').trim();

  if (!city && !address) {
    return { text: 'Localisation non renseignée', title: '', empty: true };
  }

  if (city && address) {
    return {
      text: `${city} · ${address}`,
      title: `${city} — ${address}`,
      empty: false
    };
  }

  const single = city || address;
  return { text: single, title: single, empty: false };
}

export function matchesCompanySearch(company, term, affiliations = [], cities = []) {
  if (!term) return true;
  const q = term.toLowerCase().trim();
  const ref = resolveCompanyAffiliation(company, affiliations);
  const affiliationLabel = getAffiliationShortLabelFromRef(ref).toLowerCase();
  const cityRef = resolveCompanyCity(company, cities);
  const cityLabel = (cityRef?.name || company.city || '').toLowerCase();
  return (
    company.company_code?.toLowerCase().includes(q) ||
    company.name?.toLowerCase().includes(q) ||
    company.mail?.toLowerCase().includes(q) ||
    cityLabel.includes(q) ||
    company.address?.toLowerCase().includes(q) ||
    company.phone?.includes(term.trim()) ||
    affiliationLabel.includes(q) ||
    company.affiliation?.toLowerCase().includes(q) ||
    String(company.affiliation_id || '').includes(q) ||
    String(company.city_id || '').includes(q)
  );
}

export function companyFormFromRecord(
  company,
  affiliations = [],
  defaultAffiliation = null,
  cities = [],
  defaultCity = null
) {
  const ref = resolveCompanyAffiliation(company, affiliations);
  const cityRef = resolveCompanyCity(company, cities);
  return {
    company_code: company?.company_code || '',
    name: company?.name || '',
    address: company?.address || '',
    city_id: cityRef?.id ?? company?.city_id ?? defaultCity?.id ?? '',
    phone: company?.phone || '',
    mail: company?.mail || '',
    affiliation_id: ref?.id ?? company?.affiliation_id ?? defaultAffiliation?.id ?? ''
  };
}

export function buildCompanyPayload(formData) {
  const payload = {
    company_code: String(formData.company_code || '').trim().toUpperCase(),
    name: formData.name,
    address: formData.address,
    phone: formData.phone,
    mail: formData.mail
  };
  if (formData.affiliation_id != null && String(formData.affiliation_id).trim() !== '') {
    payload.affiliation_id = parseInt(formData.affiliation_id, 10);
  }
  if (formData.city_id != null && String(formData.city_id).trim() !== '') {
    payload.city_id = parseInt(formData.city_id, 10);
  }
  return payload;
}
